import React, { useEffect, useState } from 'react';
import { Box, Button, Paper, Typography } from '@mui/material';
import { Field } from '../hook-forms/fields';
import { Form } from '../hook-forms/form-provider';
import { z as zod } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import { getCurrentSystemPrompt, getDefaultSystemPrompt, setSystemPrompt } from '../../utils/server/storageUtils';
import { showToastError, showToastSuccess } from '../../utils/common/toast';
import { ShowRecentPromptsButton } from './ShowRecentPromptsButton';
import { ModelSelectionButton } from './ModelSelectionButton';
import { useAppTranslation } from '../../hooks/useAppTranslation';
import { useAuth } from '../../../context/AuthContext';

export function PromptUpdateField() {
    const { t } = useAppTranslation();
    const { token } = useAuth();

    const UpdatePromptSchema = zod.object({
        prompt: zod.string().min(1, { message: t.fieldRequired }),
    });

    const [isLoadingPrompt, setIsLoadingPrompt] = useState(false);
    const [scopeVersion, setScopeVersion] = useState(0);
    const [loadedPrompt, setLoadedPrompt] = useState('');

    const methods = useForm({
        resolver: zodResolver(UpdatePromptSchema),
        defaultValues: { prompt: '' },
    });

    const {
        control,
        handleSubmit,
        reset,
        setValue,
        formState: { isSubmitting, isDirty },
    } = methods;

    useEffect(() => {
        const handleScopeChange = () => setScopeVersion((current) => current + 1);

        window.addEventListener('active-chatbot-changed', handleScopeChange);
        window.addEventListener('active-tenant-changed', handleScopeChange);

        return () => {
            window.removeEventListener('active-chatbot-changed', handleScopeChange);
            window.removeEventListener('active-tenant-changed', handleScopeChange);
        };
    }, []);

    useEffect(() => {
        if (!token) return;

        const fetchPrompt = async () => {
            setIsLoadingPrompt(true);
            try {
                const currentPrompt = await getCurrentSystemPrompt(token);
                const nextPrompt = currentPrompt || (await getDefaultSystemPrompt(token));
                setLoadedPrompt(nextPrompt);
                reset({ prompt: nextPrompt });
            } catch (error) {
                console.error(error);
                showToastError(t.errorLoadPrompt);
            } finally {
                setIsLoadingPrompt(false);
            }
        };

        fetchPrompt();
    }, [reset, scopeVersion, t, token]);

    const onSubmit = async (data) => {
        if (!token) {
            showToastError(t.pleaseLoginAgain);
            return;
        }

        if (!isDirty) {
            return;
        }

        setIsLoadingPrompt(true);
        try {
            await setSystemPrompt(token, data.prompt);
            setLoadedPrompt(data.prompt);
            reset({ prompt: data.prompt });
            showToastSuccess(t.successPromptUpdate);
        } catch (error) {
            console.error(error);
            showToastError(t.errorPromptUpdate);
        } finally {
            setIsLoadingPrompt(false);
        }
    };

    const handlePromptSelect = (prompt) => {
        const nextPrompt = String(prompt || '');
        setValue('prompt', nextPrompt, {
            shouldDirty: nextPrompt !== loadedPrompt,
            shouldTouch: true,
        });
    };

    const handleResetPrompt = () => {
        reset({ prompt: loadedPrompt });
    };

    return (
        <Paper
            elevation={0}
            sx={{
                width: '100%',
                maxWidth: '1180px',
                mx: 'auto',
                p: { xs: 2.1, md: 2.75 },
                borderRadius: '28px',
                border: '1px solid var(--border-color)',
                background: 'var(--surface-gradient)',
                boxShadow: '0 18px 36px rgba(15, 23, 42, 0.07)',
            }}
        >
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) auto' },
                    gap: { xs: 1.75, lg: 2.25 },
                    alignItems: { xs: 'stretch', lg: 'start' },
                    mb: 2.5,
                }}
            >
                <Box sx={{ minWidth: 0 }}>
                    <Typography
                        variant='h5'
                        sx={{
                            fontWeight: 800,
                            color: 'var(--text-strong)',
                            letterSpacing: '-0.02em',
                            wordBreak: 'break-word',
                        }}
                    >
                        {t.systemPromptTitle}
                    </Typography>
                    <Typography sx={{ mt: 0.75, color: 'var(--text-muted)', lineHeight: 1.7 }}>
                        {t.systemPromptDescription}
                    </Typography>
                </Box>

                <Box
                    sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 1.25,
                        alignItems: 'center',
                        justifyContent: { xs: 'stretch', lg: 'flex-end' },
                        width: '100%',
                        '& > *': {
                            flexGrow: { xs: 1, sm: 0 },
                        },
                    }}
                >
                    <ModelSelectionButton />
                    <ShowRecentPromptsButton onPromptSelectAction={handlePromptSelect} />
                </Box>
            </Box>

            <Form methods={methods} onSubmit={handleSubmit(onSubmit)} style={{ width: '100%' }}>
                <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 2.25 }}>
                    <Box
                        sx={{
                            width: '100%',
                            p: { xs: 0.9, md: 1.05 },
                            borderRadius: '22px',
                            border: '1px solid var(--border-color)',
                            backgroundColor: 'var(--surface-fill)',
                            boxSizing: 'border-box',
                            '& .MuiFormControl-root, & .MuiTextField-root': {
                                width: '100%',
                                maxWidth: 'none',
                            },
                            '& .MuiInputBase-root': {
                                borderRadius: '18px',
                                backgroundColor: 'var(--surface-fill-strong)',
                                alignItems: 'flex-start',
                            },
                            '& .MuiInputLabel-root.Mui-focused': {
                                color: 'var(--accent-color)',
                            },
                            '& .Mui-focused .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'var(--accent-color) !important',
                            },
                            '& textarea': {
                                minHeight: { xs: '100px !important', md: '110px !important' },
                                overflowY: 'auto !important',
                                lineHeight: 1.7,
                                resize: 'vertical',
                            },
                        }}
                    >
                        <Field.Text
                            control={control}
                            name='prompt'
                            label={t.updatePromptLabel}
                            variant='outlined'
                            disabled={isLoadingPrompt}
                            multiline
                            rows={5}
                        />
                    </Box>

                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: { xs: 'stretch', sm: 'center' },
                            gap: 1.5,
                            flexDirection: { xs: 'column', sm: 'row' },
                        }}
                    >
                        <Typography sx={{ color: isDirty ? 'var(--accent-color)' : 'var(--text-muted)', fontSize: '0.92rem', fontWeight: isDirty ? 700 : 500 }}>
                            {isDirty
                                ? t.promptStatusUnsaved
                                : t.promptStatusSaved}
                        </Typography>

                        <Box sx={{ display: 'flex', gap: 1.25, width: { xs: '100%', sm: 'auto' }, flexDirection: { xs: 'column', sm: 'row' } }}>
                            <Button
                                variant='outlined'
                                type='button'
                                onClick={handleResetPrompt}
                                disabled={!isDirty || isLoadingPrompt}
                                sx={{
                                    minHeight: 50,
                                    minWidth: { xs: '100%', sm: 132 },
                                    borderRadius: '16px',
                                    px: 2.5,
                                    fontWeight: 700,
                                    textTransform: 'none',
                                }}
                            >
                                {t.reset}
                            </Button>

                            <Button
                                variant='contained'
                                type='submit'
                                disabled={!isDirty || isSubmitting || isLoadingPrompt}
                                sx={{
                                    minHeight: 50,
                                    minWidth: { xs: '100%', sm: 164 },
                                    borderRadius: '16px',
                                    px: 3,
                                    fontWeight: 800,
                                    textTransform: 'none',
                                    background: 'var(--accent-gradient)',
                                    boxShadow: '0 12px 24px var(--accent-shadow)',
                                    '&.Mui-disabled': {
                                        color: 'rgba(255,255,255,0.72)',
                                        background: 'var(--button-disabled-bg)',
                                    },
                                }}
                            >
                                {isLoadingPrompt ? t.loading : isSubmitting ? t.saving : isDirty ? t.save : t.saved}
                            </Button>
                        </Box>
                    </Box>
                </Box>
            </Form>
        </Paper>
    );
}
